function resolveJamIframeSrc(u) {
  const jamSearch = u.search || '';
  const host = u.hostname.toLowerCase();
  const origin = u.origin;
  if (host === 'recorder.jam.dev' || host.endsWith('.recorder.jam.dev')) {
    if (u.pathname && u.pathname !== '/') return `${origin}${u.pathname}${jamSearch}`;
    return null;
  }

  const isJamAppHost = host === 'jam.dev' || (host.endsWith('.jam.dev') && !host.endsWith('recorder.jam.dev'));

  if (!isJamAppHost) return null;

  const byC = u.pathname.match(/\/c\/([^/?#]+)/i);
  if (byC) return `${origin}/c/${decodeURIComponent(byC[1])}${jamSearch}`;
  const byShare = u.pathname.match(/\/share\/([^/?#]+)/i);
  if (byShare) return `${origin}/share/${decodeURIComponent(byShare[1])}${jamSearch}`;
  const byMini = u.pathname.match(/\/mini\/([^/?#]+)/i);
  if (byMini) return `${origin}/c/${decodeURIComponent(byMini[1])}${jamSearch}`;
  return null;
}

function normalizeHttpUrl(urlString) {
  const t = String(urlString).trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (/jam\.dev/i.test(t) || /recorder\.jam\.dev/i.test(t)) return `https://${t}`;
  if (/loom\.com/i.test(t)) return `https://${t}`;
  return null;
}

export function resolveEmbedIframeSrc(urlString) {
  if (!urlString || typeof urlString !== 'string') return null;
  try {
    const raw = urlString.trim();
    let u;
    try {
      u = new URL(raw);
    } catch {
      const fixed = normalizeHttpUrl(raw);
      if (!fixed) return null;
      u = new URL(fixed);
    }
    const host = u.hostname.toLowerCase();

    const jamSrc = resolveJamIframeSrc(u);
    if (jamSrc) return jamSrc;

    if (host.includes('loom.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      const shareIdx = parts.indexOf('share');
      if (shareIdx >= 0 && parts[shareIdx + 1]) {
        return `https://www.loom.com/embed/${parts[shareIdx + 1]}${u.search}`;
      }
      const embedIdx = parts.indexOf('embed');
      if (embedIdx >= 0 && parts[embedIdx + 1]) {
        const origin = u.origin || 'https://www.loom.com';
        return `${origin}/embed/${parts[embedIdx + 1]}${u.search}`;
      }
      return null;
    }

    if (host.includes('youtube.com')) {
      const shorts = u.pathname.match(/^\/shorts\/([^/?]+)/);
      if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
      if (u.pathname.startsWith('/embed/')) {
        const id = u.pathname.split('/')[2];
        if (id) return `https://www.youtube.com/embed/${id}${u.search}`;
      }
      return null;
    }

    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
      return null;
    }

    if (host.includes('vimeo.com')) {
      const m = u.pathname.match(/\/(\d+)/);
      if (m) return `https://player.vimeo.com/video/${m[1]}${u.search}`;
      return null;
    }

    return null;
  } catch {
    return null;
  }
}

export function resolveEmbedPosterSrc(embedSrc) {
  if (!embedSrc || typeof embedSrc !== 'string') return null;
  try {
    const u = new URL(embedSrc);
    const host = u.hostname.toLowerCase();
    if (host.includes('youtube.com') || host === 'youtu.be') {
      const parts = u.pathname.split('/').filter(Boolean);
      const embedIdx = parts.indexOf('embed');
      const id = embedIdx >= 0 ? parts[embedIdx + 1] : parts[0];
      if (id) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    }
  } catch {
    return null;
  }
  return null;
}

/** oEmbed endpoint that returns thumbnail_url. Loom and Vimeo allow browser fetches. */
export function resolveEmbedOembedUrl(embedSrc) {
  if (!embedSrc || typeof embedSrc !== 'string') return null;
  try {
    const u = new URL(embedSrc);
    const host = u.hostname.toLowerCase();
    const id = u.pathname.split('/').filter(Boolean).pop();
    if (!id) return null;
    if (host.includes('loom.com')) {
      return `https://www.loom.com/v1/oembed?url=${encodeURIComponent(`https://www.loom.com/share/${id}`)}`;
    }
    if (host.includes('vimeo.com')) {
      return `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}`;
    }
  } catch {
    return null;
  }
  return null;
}

export function isDirectVideoUrl(urlString) {
  if (!urlString || typeof urlString !== 'string') return false;
  if (urlString.startsWith('blob:')) return true;
  try {
    const path = new URL(urlString).pathname.toLowerCase();
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(path);
  } catch {
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(urlString);
  }
}

export function isCourseAttachmentVideoStreamUrl(urlString) {
  if (!urlString || typeof urlString !== 'string') return false;
  const re = /\/courses\/[a-fA-F0-9]{24}\/attachments\/[a-fA-F0-9]{24}(?:\/stream)?(?:\/|\?|$)/i;
  try {
    return re.test(new URL(urlString).pathname);
  } catch {
    return re.test(urlString);
  }
}

export function canPlayVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return Boolean(resolveEmbedIframeSrc(url) || isDirectVideoUrl(url) || isCourseAttachmentVideoStreamUrl(url));
}
